import type { ErrorEnvelope } from "@playbookai/types";
import { PlaybookApiError } from "./errors.js";
import type { ApiClientConfig } from "./client-config.js";
import { buildApiUrl, type ApiQuery } from "./url.js";

/**
 * Generic typed fetch wrapper for playbook-be. Each module feature package's
 * domain client (`solvesApi`, `hoshinPlansApi`, …) calls into `apiCall`.
 *
 * Graduated here from `solve-feature/src/api` (PB-263) once the second
 * live-fetch client (hoshin's) needed it — keeping it in a sibling feature would
 * be the ADR-0009 module→sibling anti-pattern. Behaviour is unchanged: BFF
 * cookies + CSRF, one reactive refresh-and-retry on 401, `204`→`undefined`, and
 * a typed `PlaybookApiError` on any non-2xx.
 */
export async function apiCall<TRes, TBody = unknown>(
  config: ApiClientConfig,
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: TBody,
  // `ApiQuery`, not an inline record: keeps this signature in lockstep with
  // buildApiUrl's accepted values (array values serialize as repeated keys).
  query?: ApiQuery,
  // Compatibility ceiling: the next cross-cutting request option requires an
  // ApiCallOptions migration rather than a seventh positional parameter.
  signal?: AbortSignal,
): Promise<TRes> {
  const url = buildApiUrl(config.baseUrl, path, query);

  const isStateChanging = method !== "GET";
  const buildHeaders = (token: string | null): Record<string, string> => {
    const h: Record<string, string> = { Accept: "application/json" };
    if (body !== undefined) h["Content-Type"] = "application/json";
    if (token) h.Authorization = `Bearer ${token}`;
    if (isStateChanging && config.getCsrfToken) {
      const csrf = config.getCsrfToken();
      if (csrf) h["X-CSRF-Token"] = csrf;
    }
    return h;
  };

  const buildInit = (token: string | null): RequestInit => {
    const init: RequestInit = { method, headers: buildHeaders(token) };
    if (signal) init.signal = signal;
    if (body !== undefined) init.body = JSON.stringify(body);
    if (config.useCookies) init.credentials = "include";
    return init;
  };

  let response = await fetch(url, buildInit(config.getAccessToken()));

  // One reactive refresh-and-retry on 401.
  //
  // The retry is keyed on the refresh RESOLVING, not on it handing back a
  // token (PB-4900). In BFF mode the credential is a cookie, so a successful
  // renew has no bearer to return and resolves with `null` — under the old
  // truthiness check that read as failure, which made this branch unreachable
  // in production and left every recoverable 401 surfacing as an error.
  //
  // A rejected refresh means the session is gone or the verdict never arrived;
  // either way there is nothing to retry with. The caller that owns the session
  // decides what a failure means — this layer only declines to retry.
  if (response.status === 401 && config.refreshAccessToken) {
    let refreshed: string | null | undefined;
    let renewed = true;
    try {
      refreshed = await config.refreshAccessToken();
    } catch {
      renewed = false;
    }
    if (renewed) {
      response = await fetch(url, buildInit(refreshed ?? null));
    }
  }

  if (response.status === 204) {
    return undefined as unknown as TRes;
  }

  const ct = response.headers.get("content-type") ?? "";
  const data = ct.includes("application/json")
    ? ((await response.json()) as unknown)
    : ((await response.text()) as unknown);

  if (!response.ok) {
    const envelope = isErrorEnvelope(data)
      ? data
      : ({
          code: "INTERNAL_ERROR",
          message: typeof data === "string" ? data : "Request failed.",
        } as ErrorEnvelope);
    if (isModuleUnavailableEnvelope(envelope) && config.onModuleUnavailable) {
      // The hook only refreshes the module list; a fault in it must never
      // replace the refusal the caller is about to handle.
      try {
        config.onModuleUnavailable();
      } catch {
        // Intentionally ignored — see above.
      }
    }
    throw new PlaybookApiError(response.status, envelope);
  }

  return data as TRes;
}

/**
 * Whether a refusal says the module is not available to this workspace
 * (ADR-0100). Two spellings, one meaning: the gateway answers with the code
 * itself; playbook-be keeps its own code (`INSUFFICIENT_PRIVILEGE`,
 * `WORK_LINK_MODULE_UNAVAILABLE`) and puts the reason in `details`.
 */
export function isModuleUnavailableEnvelope(envelope: ErrorEnvelope): boolean {
  return (
    envelope.code === "module_unavailable" ||
    envelope.details?.["reason"] === "module_unavailable"
  );
}

function isErrorEnvelope(v: unknown): v is ErrorEnvelope {
  return (
    typeof v === "object" &&
    v !== null &&
    typeof (v as { code?: unknown }).code === "string" &&
    typeof (v as { message?: unknown }).message === "string"
  );
}
